// Legal pages — short, plain-language summaries.
// These are provided in good faith for community use. Before going to wide
// public launch you should have an attorney review and replace.

import Seo from "@/components/Seo";

export function Terms() {
  return (
    <div className="max-w-3xl mx-auto px-6 py-16 prose-jj">
      <Seo
        title="Terms of Service · Jesus Just Us"
        description="The Terms of Service for the Jesus Just Us community platform — accounts, content ownership, moderation, payments, and acceptable use."
        path="/terms"
      />
      <div className="overline text-burgundy">Terms of Service</div>
      <h1 className="font-serif text-5xl text-jjblue mt-3">Terms of Service</h1>
      <p className="text-xs text-[var(--jj-muted)] mt-2">Last updated: February 2026</p>

      <div className="mt-8 space-y-6 text-[var(--jj-text-secondary)] leading-relaxed">
        <Section title="1. Who we are">
          Jesus Just Us ("JJU", "we", "our") is a community platform owned and operated by its
          founder. By accessing or using the platform you agree to these Terms.
        </Section>
        <Section title="2. Your account">
          You must be at least 13 years old. You're responsible for keeping your password safe and for
          activity on your account. Provide accurate information; impersonation isn't allowed.
        </Section>
        <Section title="3. Acceptable use">
          You agree not to post content that is illegal, hateful, harassing, exploitative, deceptive,
          sexually explicit, or that infringes on others' rights. JJU is faith-rooted and family-safe.
        </Section>
        <Section title="4. Your content">
          You keep ownership of what you post. By posting, you grant JJU a non-exclusive license to
          display and distribute it on the platform. You can delete your content at any time from your
          profile.
        </Section>
        <Section title="5. Marketplace and donations">
          Paid features (memberships, donations, marketplace) are processed by Stripe. We do not store
          card numbers. Refunds for memberships are pro-rated; donations are non-refundable except in
          cases of clear error.
        </Section>
        <Section title="6. Moderation">
          We may remove content or suspend accounts that violate these Terms or our Community
          Guidelines. We log moderation decisions.
        </Section>
        <Section title="7. Termination">
          You may close your account anytime via the Contact page. We may suspend or terminate accounts
          that abuse the platform or harm the community.
        </Section>
        <Section title="8. Disclaimers">
          JJU is provided "as is." We don't guarantee uninterrupted service. JJU is not a substitute for
          professional medical, legal, financial, or emergency services. In an emergency, call 911.
        </Section>
        <Section title="9. Changes">
          We may update these Terms. We'll post the updated version here and, for material changes,
          notify you in-app.
        </Section>
        <Section title="10. Contact">
          Questions about these Terms? Use the <a href="/contact" className="text-burgundy font-medium">Contact</a> page.
        </Section>
      </div>
    </div>
  );
}

export function Privacy() {
  return (
    <div className="max-w-3xl mx-auto px-6 py-16">
      <Seo
        title="Privacy Policy · Jesus Just Us"
        description="How Jesus Just Us collects, uses, and protects your information. We don't sell data, don't show ads, and don't read your private messages."
        path="/privacy"
      />
      <div className="overline text-burgundy">Privacy</div>
      <h1 className="font-serif text-5xl text-jjblue mt-3">Privacy Policy</h1>
      <p className="text-xs text-[var(--jj-muted)] mt-2">Last updated: February 2026</p>

      <div className="mt-8 space-y-6 text-[var(--jj-text-secondary)] leading-relaxed">
        <Section title="What we collect">
          Account info you give us (name, email, password — hashed, never readable by us). Content you
          post (text, photos, audio, video, documents). Basic usage data (logins, page visits) for
          safety and improvement.
        </Section>
        <Section title="What we don't do">
          We do not sell your data. We don't show ads. We don't read your private messages.
        </Section>
        <Section title="Cookies and storage">
          We use one secure cookie to keep you signed in. We don't use tracking cookies.
        </Section>
        <Section title="Payments">
          Payments are processed by Stripe. We never see or store full card numbers. We retain payment
          metadata (amount, timestamp, designation) for receipts and bookkeeping.
        </Section>
        <Section title="Your rights">
          You can edit or delete your content anytime. You can request full account deletion via the
          <a href="/contact" className="text-burgundy font-medium"> Contact</a> page; we'll remove your data within 7 days (except records we're legally required
          to keep, like tax-relevant donation records).
        </Section>
        <Section title="Security">
          Passwords are hashed with bcrypt. We use HTTPS everywhere. We restrict admin access to
          sensitive data with audit logs.
        </Section>
        <Section title="Children">
          JJU is for ages 13 and up. If you believe a child under 13 has registered, contact us and
          we'll remove the account.
        </Section>
        <Section title="Changes">
          If we update this policy, we'll post it here. Significant changes will be announced in-app.
        </Section>
      </div>
    </div>
  );
}

export function Guidelines() {
  return (
    <div className="max-w-3xl mx-auto px-6 py-16">
      <Seo
        title="Community Guidelines · Jesus Just Us"
        description="A sanctuary for the body of Christ. Our community guidelines on love, honesty, safety, and accountability — applied to founders and members alike."
        path="/guidelines"
      />
      <div className="overline text-burgundy">Community</div>
      <h1 className="font-serif text-5xl text-jjblue mt-3">Community Guidelines</h1>
      <p className="text-xs text-[var(--jj-muted)] mt-2">A living document, updated as our family grows.</p>

      <p className="mt-6 text-[var(--jj-text-secondary)] leading-relaxed">
        Jesus Just Us is a sanctuary for the body of Christ. These guidelines exist so it stays that way.
      </p>

      <div className="mt-8 space-y-5">
        <Rule title="Love one another." body="Speak as you would speak in front of your grandmother, your pastor, and the Lord — because all three are watching." />
        <Rule title="No hate. No harassment." body="Personal attacks, slurs, bullying, dog-piling — none of it is welcome here. Disagree about ideas, never devalue people." />
        <Rule title="No scams or exploitation." body="Don't solicit funds outside our approved donation flows. Don't manipulate the vulnerable. Multi-level marketing pitches will be removed." />
        <Rule title="No unsafe advice." body="Don't give medical, legal, or financial advice that could harm someone. Encourage professional help where it's needed." />
        <Rule title="No illegal activity." body="Selling regulated goods, promoting violence, sharing illegal content — instant removal." />
        <Rule title="Protect minors fiercely." body="Any predatory behavior, grooming, or inappropriate content involving minors is reported to authorities immediately." />
        <Rule title="Tell the truth." body="Don't misrepresent yourself, your organization, your products, or your intentions. Authenticity is everything." />
        <Rule title="Honor privacy." body="Don't share screenshots of private conversations or private group content without permission." />
        <Rule title="Report rather than retaliate." body="If something feels off, click report. Our moderators handle it. Don't take the law into your own hands." />
      </div>

      <p className="mt-10 text-[var(--jj-text-secondary)] leading-relaxed">
        Members who repeatedly violate these guidelines will be warned, then suspended, then permanently removed.
        These rules apply to the founder and admins too — accountability runs all directions.
      </p>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="font-serif text-2xl text-jjblue">{title}</h2>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function Rule({ title, body }) {
  return (
    <div className="jj-card p-5">
      <div className="font-serif text-xl text-jjblue">{title}</div>
      <p className="text-sm text-[var(--jj-text-secondary)] mt-2 leading-relaxed">{body}</p>
    </div>
  );
}
